import React from "react";
import RegisterPage, { STATUS_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { ChartCard, ChartTooltip } from "../components/ChartCard";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, ComposedChart } from "recharts";

export default function Waste() {
  const { data, siteNames } = useHSE();
  const rows = data.waste || [];
  const totalGen = rows.reduce((a, b) => a + Number(b.generated || 0), 0).toFixed(1);
  const totalRec = rows.reduce((a, b) => a + Number(b.recycled || 0), 0).toFixed(1);
  const recyclingPct = totalGen > 0 ? ((totalRec / totalGen) * 100).toFixed(1) : 0;
  const spillEvents = rows.filter((r) => r.event && r.event.includes("Spill")).length;

  const monthly = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m) => {
    const arr = rows.filter((w) => w.month === m);
    return {
      month: m,
      Generated: +arr.reduce((a, b) => a + Number(b.generated || 0), 0).toFixed(1),
      Recycled: +arr.reduce((a, b) => a + Number(b.recycled || 0), 0).toFixed(1),
    };
  });

  const Top = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="grid grid-cols-2 gap-3 lg:col-span-1">
        <StatCard label="Generated" value={`${totalGen} T`} tone="warning" />
        <StatCard label="Recycled" value={`${totalRec} T`} tone="safe" />
        <StatCard label="Recycling Rate" value={`${recyclingPct}%`} tone={recyclingPct >= 60 ? "safe" : "warning"} />
        <StatCard label="Spill Events" value={spillEvents} tone={spillEvents > 0 ? "critical" : "safe"} />
      </div>
      <div className="lg:col-span-2">
        <ChartCard title="Waste Generated vs Recycled" subtitle="Tonnes per month">
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fill: "#455560", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#455560", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Generated" fill="#082B4D" radius={[4, 4, 0, 0]} />
              <Line dataKey="Recycled" stroke="#18B84F" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );

  return (
    <RegisterPage
      title="Waste / Environment"
      subtitle="Waste generation, recycling, spills, environmental events — ISO 14001 aligned"
      storeKey="waste"
      idPrefix="WST"
      topPanels={Top}
      columns={[
        { key: "id", label: "ID" },
        { key: "month", label: "Month" },
        { key: "site", label: "Site" },
        { key: "type", label: "Waste Type" },
        { key: "generated", label: "Generated (T)" },
        { key: "recycled", label: "Recycled (T)" },
        { key: "recyclingPct", label: "Recycle %", render: (r) => <span className="tabular font-semibold text-[#18B84F]">{r.recyclingPct}%</span> },
        { key: "disposalMethod", label: "Disposal Method" },
        { key: "vendor", label: "Waste Vendor" },
        { key: "event", label: "Env. Event" },
        { key: "status", label: "Status" },
      ]}
      badgeMap={{ status: STATUS_BADGES }}
      formFields={[
        { key: "id", label: "Record ID" },
        { key: "month", label: "Month", type: "select", options: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "type", label: "Waste Type", type: "select", options: data.settings.wasteTypes || [] },
        { key: "generated", label: "Quantity Generated (Tonnes)", type: "number" },
        { key: "recycled", label: "Quantity Recycled (Tonnes)", type: "number" },
        { key: "recyclingPct", label: "Recycling %", type: "number" },
        { key: "disposalMethod", label: "Disposal Method" },
        { key: "vendor", label: "Waste Vendor" },
        { key: "event", label: "Environmental Event", type: "select", options: ["None", "Minor Spill", "Major Spill", "Environmental Incident", "Recycling Drive", "Audit"] },
        { key: "waterConsumption", label: "Water Consumption (m³)", type: "number" },
        { key: "energyConsumption", label: "Energy Consumption (kWh)", type: "number" },
        { key: "co2Emissions", label: "CO2 Emissions (kg CO2e)", type: "number" },
        { key: "description", label: "Description / Notes", type: "textarea", full: true },
        { key: "status", label: "Status", type: "select", options: ["Recorded", "Verified"] },
      ]}
    />
  );
}
